import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { api } from "../lib/api";
import { BUSINESS_TYPES, PRODUCT_OPTIONS } from "../lib/data";

const STORY_SLIDES = [
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/obwgq0gk_AdobeStock_444199902.webp", alt: "Kraft paper salad bowl with tomatoes and croutons" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/jfii9597_AdobeStock_579011444.webp", alt: "Two kraft paper bowls filled with rice and vegetables" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/3am6ra1q_AdobeStock_1520399972.webp", alt: "White paper soup bowl with veggies flatlay" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/gl8cwemn_AdobeStock_1520402546.webp", alt: "Paper cup and lid on wood with oats" },
    { src: "/generated/bowl_machine.png", alt: "Industrial paper bowl forming machine at The Puro Pack" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/4hvsl06m_AdobeStock_491552839.webp", alt: "White paper soup bowl with lid" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/yry5zcyf_AdobeStock_1529584138.webp", alt: "Range of Puro Pack kraft bowls, cups and drinks arranged as a flatlay" },
];

const initial = {
    company_name: "", contact_person: "", email: "", mobile: "",
    country: "", business_type: "", products_interested: [], monthly_requirement: "", message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [storyIdx, setStoryIdx] = useState(0);
    const fileRef = useRef();

    useEffect(() => {
        const t = setInterval(() => setStoryIdx(i => (i + 1) % STORY_SLIDES.length), 4500);
        return () => clearInterval(t);
    }, []);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggleProduct = (p) => setForm((f) => ({
        ...f,
        products_interested: f.products_interested.includes(p)
            ? f.products_interested.filter((x) => x !== p)
            : [...f.products_interested, p],
    }));

    async function onSubmit(e) {
        e.preventDefault();
        const required = ["company_name", "contact_person", "email", "mobile", "country", "business_type", "monthly_requirement"];
        for (const k of required) if (!form[k]) { toast.error(`Please fill in ${k.replace(/_/g, " ")}`); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => {
                if (k === "products_interested") fd.append(k, v.join(","));
                else fd.append(k, v);
            });
            if (file) fd.append("file", file);
            await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setSubmitted(true);
            setForm(initial);
            setFile(null);
            toast.success("Enquiry received — we'll get back to you within 24 hours.");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div data-testid="contact-page">
            <Seo title="Contact" description="Talk to The Puro Pack about sustainable paper packaging — samples, custom printing and export enquiries. Based in Bangalore, shipping worldwide." path="/contact" />
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
                <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact", to: "/contact" }]} />
            </div>

            {/* CINEMATIC STORY SLIDESHOW (auto-playing Ken-Burns loop) */}
            <section className="relative w-full h-[70vh] min-h-[500px] overflow-hidden bg-inkgreen border-b border-forest" data-testid="contact-video">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={STORY_SLIDES[storyIdx].src}
                        src={STORY_SLIDES[storyIdx].src}
                        alt={STORY_SLIDES[storyIdx].alt}
                        initial={{ opacity: 0, scale: 1.06 }}
                        animate={{ opacity: 1, scale: 1.14 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: 1.2, ease: "easeInOut" }, scale: { duration: 5, ease: "linear" } }}
                        className="absolute inset-0 w-full h-full object-cover"
                        draggable={false}
                    />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-inkgreen/90 via-inkgreen/30 to-inkgreen/55 pointer-events-none" />
                <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-10 flex flex-col justify-end py-14 pointer-events-none">
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-bone/80 mb-4">[ Our story · in loops ]</div>
                    <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight text-bone max-w-4xl">
                        Sustainability, <em className="italic">served fresh.</em>
                    </h2>
                    <div className="mt-8 flex gap-1.5" data-testid="contact-story-dots">
                        {STORY_SLIDES.map((_, i) => (
                            <span key={i} className={`h-[2px] transition-all ${i === storyIdx ? "w-10 bg-bone" : "w-5 bg-bone/40"}`} />
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">[ Enquire · Bangalore ]</div>
                    <h1 className="font-serif text-6xl lg:text-8xl leading-[0.92] tracking-tight text-forest">Let&apos;s <em className="italic">talk</em> packaging.</h1>
                    <p className="mt-8 text-inkgreen/80 leading-relaxed max-w-md">
                        Fill in the enquiry and we&apos;ll respond within 24 hours with samples, pricing and MOQs relevant to your kitchen or brand.
                    </p>
                    <div className="mt-12 border-t border-forest/30 pt-6 space-y-3">
                        <div className="font-mono text-xs uppercase tracking-widest text-stone">Factory</div>
                        <div className="text-inkgreen">Bangalore, Karnataka, India</div>
                        <div className="font-mono text-xs uppercase tracking-widest text-stone pt-6">Response time</div>
                        <div className="text-inkgreen">Within 24 hours, Monday–Saturday</div>
                    </div>
                    <div className="mt-8 border border-forest/30" data-testid="contact-map">
                        <iframe
                            title="The Puro Pack — Bangalore location"
                            src="https://www.google.com/maps?q=Bangalore%20Karnataka%20India&output=embed"
                            width="100%"
                            height="260"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                        />
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {submitted ? (
                        <div className="border border-forest p-12 flex flex-col items-start gap-4" data-testid="contact-success">
                            <Check className="text-clay w-10 h-10" />
                            <h2 className="font-serif text-4xl text-forest">Thank you.</h2>
                            <p className="text-inkgreen/80">We've received your enquiry. A member of our team will respond within one business day.</p>
                            <button onClick={() => setSubmitted(false)} className="btn-pill mt-4">Send another →</button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="contact-form">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Company Name *</label>
                                <input required data-testid="contact-company-name" value={form.company_name} onChange={update("company_name")} className="print-input" placeholder="Your company" />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Contact Person *</label>
                                <input required data-testid="contact-contact-person" value={form.contact_person} onChange={update("contact_person")} className="print-input" placeholder="Full name" />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Email *</label>
                                <input required type="email" data-testid="contact-email" value={form.email} onChange={update("email")} className="print-input" placeholder="you@company.com" />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Mobile Number *</label>
                                <input required data-testid="contact-mobile" value={form.mobile} onChange={update("mobile")} className="print-input" placeholder="+91..." />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Country *</label>
                                <input required data-testid="contact-country" value={form.country} onChange={update("country")} className="print-input" placeholder="India, UAE, UK…" />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Business Type *</label>
                                <select required data-testid="contact-business-type" value={form.business_type} onChange={update("business_type")} className="print-input">
                                    <option value="">Select…</option>
                                    {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Products Interested In</label>
                                <div className="flex flex-wrap gap-2 mt-3" data-testid="contact-products">
                                    {PRODUCT_OPTIONS.map((p) => {
                                        const active = form.products_interested.includes(p);
                                        return (
                                            <button type="button" key={p} onClick={() => toggleProduct(p)} data-testid={`contact-product-${p.toLowerCase().replace(/\s+/g, '-')}`}
                                                className={`px-4 py-2 rounded-full border text-sm transition-colors ${active ? "bg-forest text-bone border-forest" : "border-forest/40 text-forest hover:border-forest"}`}>
                                                {p}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Monthly Requirement *</label>
                                <input required data-testid="contact-monthly" value={form.monthly_requirement} onChange={update("monthly_requirement")} className="print-input" placeholder="e.g. 50,000 units / month" />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Attach Logo / Requirement File</label>
                                <div className="flex items-center gap-3 pt-3">
                                    <button type="button" onClick={() => fileRef.current?.click()} className="btn-pill" data-testid="contact-file-upload">
                                        <Upload className="w-4 h-4" /> {file ? file.name.slice(0, 24) : "Choose file"}
                                    </button>
                                    <input ref={fileRef} type="file" accept="image/*,.pdf,.ai,.eps,.zip" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="font-mono text-xs uppercase tracking-widest text-stone">Message</label>
                                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} className="print-input" rows={4} placeholder="Tell us about your brand and packaging needs" />
                            </div>
                            <div className="md:col-span-2 pt-4">
                                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-pill btn-solid">
                                    {submitting ? "Sending…" : "Submit Enquiry →"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
